// The two different things called "department", and how to tell them apart.
//
// There is ONE `departments` table. Migration 017 folded the hiring hierarchy
// into it, and migration 015_org_dept_type then split the notion of a department
// in two, because one list was serving two unrelated purposes: departments
// invented for ticket routing were turning up on the HR/org-chart screens, and
// HR departments were turning up in the ticket dropdown.
//
//   dept      a TICKET department. The dropdown when raising a ticket.
//   org_dept  an ORG department. A root of the org chart / HR module.
//   dept_org  BOTH — one row that appears in the ticket dropdown and in the org
//             chart. This is what a department genuinely used by both sides is,
//             and what every already-in-hiring-use `dept` row was promoted to by
//             the migration, so neither side lost anything.
//
// `project` is a ticketing project and is unrelated to either.
// `div` / `team` / `role` / `grade` are the rungs *inside* an org department.
//
// Nothing should compare `type` to a bare string. Use the helpers here so the
// two sides cannot drift apart, and so a future third scope is one edit.
// Mirrors Department.TICKET_DEPT_TYPES / ORG_ROOT_TYPES in the backend model.

export const TICKET_DEPT_TYPES = ["dept", "dept_org"];
export const ORG_DEPT_TYPES = ["org_dept", "dept_org"];
export const PROJECT_TYPES = ["project"];

// Rows that are a department of some kind, either side. Used by screens that
// manage the whole list rather than consume one side of it.
export const ANY_DEPT_TYPES = ["dept", "org_dept", "dept_org"];

export const isTicketDept = (row) => TICKET_DEPT_TYPES.includes(row?.type);
export const isOrgDept = (row) => ORG_DEPT_TYPES.includes(row?.type);
export const isProject = (row) => PROJECT_TYPES.includes(row?.type);
export const isAnyDept = (row) => ANY_DEPT_TYPES.includes(row?.type);

// What the create/edit forms offer, in the order they should be shown.
//
// The wording is deliberately about where the department shows up rather than
// about the column value: "org_dept" means nothing to the person filling the
// form in, and picking the wrong one is only obvious once a department is
// missing from a screen someone else uses.
export const DEPT_SCOPES = [
  {
    value: "dept",
    label: "Tickets only",
    description: "Appears when raising a ticket. Not on the org chart.",
  },
  {
    value: "org_dept",
    label: "Organization only",
    description: "A root of the org chart. Not in the ticket dropdown.",
  },
  {
    value: "dept_org",
    label: "Both",
    description: "In the ticket dropdown and on the org chart.",
  },
];

// Just the two an org-chart screen may write. It can mark a department as also
// being a ticket department, but it will not create a ticket-only one — that row
// would disappear from the screen that made it. Mirrors CREATABLE_TYPES in the
// backend's departmentController.
export const ORG_DEPT_SCOPES = DEPT_SCOPES.filter((scope) =>
  ORG_DEPT_TYPES.includes(scope.value),
);

export const scopeLabel = (type) =>
  DEPT_SCOPES.find((scope) => scope.value === type)?.label || null;
